import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';

/**
 * Group raw-material inventory rows by product_code (货号) for charting.
 *
 * Notes on aggregation:
 *   - remaining_quantity is summed across locations/clients (total physical stock).
 *   - purchase_pending_quantity is the *product-level* outstanding PO quantity.
 *     The backend attaches the same total to every location row for a product,
 *     so we must count it once per product_id to avoid double/triple counting.
 *   - purchase_pending_po_ids are de-duplicated across the group.
 */
export const aggregateRawMaterialChartData = (data) => {
  if (!data || data.length === 0) {
    return [];
  }

  const groupedMap = new Map();
  data.forEach(item => {
    const key = item.product_code || item.product_id;
    if (!groupedMap.has(key)) {
      groupedMap.set(key, {
        product_id: item.product_id,
        product_code: item.product_code,
        name: item.name,
        specification: item.specification,
        category: item.category,
        unit: item.unit,
        tip_diameter: item.tip_diameter,
        remaining_quantity: 0,
        purchase_pending_quantity: 0,
        purchase_pending_po_ids: [],
        // Internal tracker so pending is only added once per distinct product_id.
        _pendingProductIds: new Set()
      });
    }
    const group = groupedMap.get(key);
    group.remaining_quantity += item.remaining_quantity || 0;

    const pid = item.product_id;
    if (pid && !group._pendingProductIds.has(pid)) {
      group._pendingProductIds.add(pid);
      group.purchase_pending_quantity += item.purchase_pending_quantity || 0;
      if (Array.isArray(item.purchase_pending_po_ids)) {
        item.purchase_pending_po_ids.forEach(poId => {
          if (!group.purchase_pending_po_ids.includes(poId)) {
            group.purchase_pending_po_ids.push(poId);
          }
        });
      }
    }
  });

  // Sort by tip_diameter ascending (left to right)
  return Array.from(groupedMap.values())
    .map(({ _pendingProductIds, ...item }) => item)
    .sort((a, b) => {
      const da = a.tip_diameter != null ? parseFloat(a.tip_diameter) : Infinity;
      const db = b.tip_diameter != null ? parseFloat(b.tip_diameter) : Infinity;
      return da - db;
    });
};

/**
 * RawMaterialStockChart
 *
 * Displays a bar chart of raw material stock quantities.
 * Supports zoom (mouse wheel) and pan (drag) via ECharts dataZoom.
 *
 * Props:
 *   - data: Array of inventory items with fields:
 *       { product_id, name, specification, category, unit, product_code,
 *         location, remaining_quantity, reserved_quantity, available_quantity }
 *   - loading: Boolean
 *   - excludeZeroStock: Boolean (affects empty state messaging)
 */
const RawMaterialStockChart = ({ data, loading, excludeZeroStock = false }) => {
  const chartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        title: {
          text: '暂无数据',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#888', fontSize: 16 }
        }
      };
    }

    const sortedData = aggregateRawMaterialChartData(data);

    const categories = sortedData.map(item => item.specification || item.name || item.product_code || item.product_id);
    const remainingValues = sortedData.map(item => item.remaining_quantity);
    const pendingValues = sortedData.map(item => item.purchase_pending_quantity);

    // Determine stock segment color based on stock level
    const getStockColor = (value) => {
      if (value <= 0) return '#ef4444'; // red - out of stock
      if (value < 10) return '#f59e0b'; // amber - low stock
      return '#3b82f6'; // blue - healthy
    };

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const idx = params[0]?.dataIndex ?? 0;
          const item = sortedData[idx];
          if (!item) return '';
          const stockParam = params.find(p => p.seriesName === '库存数量');
          const pendingSection = item.purchase_pending_quantity > 0
            ? `
              <div style="margin-top:2px">
                <span style="display:inline-block;width:10px;height:10px;background:#a855f7;border-radius:50%;margin-right:4px"></span>
                采购未收货: <strong>${item.purchase_pending_quantity} ${item.unit}</strong>
              </div>
              ${item.purchase_pending_po_ids.length > 0 ? `<div style="margin-left:14px;color:#888">PO: ${item.purchase_pending_po_ids.join(', ')}</div>` : ''}
            `
            : '';
          return `
            <div style="font-weight:600;margin-bottom:4px">${item.specification || item.name}</div>
            <div>产品编号: ${item.product_id}</div>
            <div>货号: ${item.product_code || '-'}</div>
            <div>分类: ${item.category || '-'}</div>
            ${item.tip_diameter != null ? `<div>刃径: ${item.tip_diameter} mm</div>` : ''}
            <div style="margin-top:4px">
              <span style="display:inline-block;width:10px;height:10px;background:${stockParam?.color || '#3b82f6'};border-radius:50%;margin-right:4px"></span>
              库存数量: <strong>${stockParam?.value ?? item.remaining_quantity} ${item.unit}</strong>
            </div>
            ${pendingSection}
          `;
        }
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: 11,
          color: '#666'
        },
        axisTick: { alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        name: '数量',
        nameTextStyle: { padding: [0, 0, 0, 20] }
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
          preventDefaultMouseMove: false
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: 10,
          height: 20,
          handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '80%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.6)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          textStyle: { color: '#666' },
          borderColor: '#ddd',
          fillerColor: 'rgba(59, 130, 246, 0.1)'
        }
      ],
      series: [
        {
          name: '库存数量',
          type: 'bar',
          stack: 'total',
          data: remainingValues,
          itemStyle: {
            color: (params) => getStockColor(params.value),
            borderRadius: (params) => {
              const item = sortedData[params.dataIndex];
              // Round top only when there is no pending segment above it
              return item?.purchase_pending_quantity > 0 ? [0, 0, 0, 0] : [4, 4, 0, 0];
            }
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          },
          animationDelay: (idx) => idx * 10
        },
        {
          name: '采购未收货',
          type: 'bar',
          stack: 'total',
          data: pendingValues,
          itemStyle: {
            color: '#a855f7',
            borderRadius: (params) => params.value > 0 ? [4, 4, 0, 0] : [0, 0, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          },
          animationDelay: (idx) => idx * 10 + 50
        }
      ],
      animationEasing: 'elasticOut',
      animationDelayUpdate: (idx) => idx * 5
    };
  }, [data]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <span className="text-muted-foreground">加载图表数据...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              {excludeZeroStock ? '没有符合条件的原材料库存数据' : '暂无原材料库存数据'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {excludeZeroStock ? '请尝试取消"隐藏零库存"选项' : '请先入库一些原材料'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">原材料库存图表</h3>
            <p className="text-sm text-muted-foreground">
              按货号聚合 · 共 {chartOption.series?.[0]?.data?.length ?? 0} 个规格 · 紫色段为采购未收货 · 鼠标滚轮缩放 · 拖拽平移 · 底部滑块缩放
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 mb-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-500" />
            库存充足
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-amber-500" />
            库存不足
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-red-500" />
            缺货
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-sm bg-purple-500" />
            采购未收货
          </span>
        </div>
        <ReactECharts
          option={chartOption}
          style={{ height: 500, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={true}
        />
      </CardContent>
    </Card>
  );
};

export default RawMaterialStockChart;
